<template>
  <CreateRealtyModal
    :customCallButton="customCallButton"
    ref="createRealtyModalWrapper"
    type="realty"
  >

    <h-loading v-show="isRealtyLoading" class="mw-450 mx-auto" />

    <v-form
      v-model="valid"
      v-show="!isRealtyLoading"
      class="mw-550 mx-auto pb-10"
      ref="realtyForm"
    >
      <v-select
        v-if="isServiceProviderUser && !realty.id && !fromAppointment"
        v-model="customer"
        :label="$t('realty.formCreate.realEstate')"
        item-text="name"
        item-value="id"
        :items="serviceProviderCustomers"
        return-object
        outlined
      />

      <div v-if="!isCodeHidden" class="input__wrapper mw-300">
        <v-text-field
          @input="checkRealtyCode"
          v-model="code"
          :rules="[rules.code, rules.required]"
          :label="isCodeReadonly ? $t('realty.label.code') : $t('realty.formCreate.code')"
          :error-messages="isRealtyCodeExists ? $t('realty.warnings.codeExistsAlready') : ''"
          :readonly="isCodeReadonly"
          :hint="isCodeReadonly ? $t('realty.hint.codeFromVista') : ''"
          :persistent-hint="isCodeReadonly"
          :append-icon="isCodeReadonly ? 'mdi-lock-outline' : ''"
          outlined
        />

        <v-progress-circular
          v-if="isLoadingCheck"
          class="input__progress"
          color="primary"
          width="2"
          size="15"
          indeterminate
          absolute
        />
      </div>

      <v-textarea
        v-model="description"
        :label="$t('realty.formCreate.description')"
        hide-details="auto"
        outlined
      />

      <h2 class="mt-10 mb-2">
        {{ $t('realty.formCreate.titles.details') }}
      </h2>

      <v-row>
        <v-col :class="realty.id ? 'grey lighten-4 rounded-lg mb-5' : ''">
          <v-row>
            <v-col cols="12" sm="5">
              <v-select
                v-model="painting_status"
                :label="$t('realty.formCreate.paintingStatus')"
                :rules="[rules.required]"
                :items="paintingStatus"
                hide-details="auto"
                item-value="value"
                item-text="name"
                outlined
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-select
                v-model="furnishing"
                :label="$t('realty.formCreate.furnished')"
                :items="furnishingOptions"
                hide-details="auto"
                item-value="value"
                item-text="name"
                outlined
              />
            </v-col>
            <v-col cols="12" sm="3">
              <v-spacer v-if="!$vuetify.breakpoint.mobile" class="mt-3" />
              <v-checkbox
                v-model="trousseau"
                :label="$t('realty.formCreate.trousseau')"
                hide-details="auto"
                outlined
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="4">
              <v-text-field
                v-model="square_meters"
                :label="$t('realty.formCreate.totalArea')"
                :rules="validSize"
                class="input__textual-icon input__text-right"
                hide-details="auto"
                append-icon="m²"
                type="number"
                outlined
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="private_area_meters"
                :label="$t('realty.formCreate.privateArea')"
                :rules="validSize"
                class="input__textual-icon input__text-right"
                hide-details="auto"
                append-icon="m²"
                type="number"
                outlined
              />
            </v-col>
            <v-col cols="4">
              <v-select
                v-model="realty_type"
                :label="$t('realty.formCreate.realtyType')"
                :rules="[rules.required]"
                :items="realtyTypes"
                hide-details="auto"
                item-value="value"
                item-text="name"
                outlined
              />
            </v-col>
          </v-row>

          <span
            v-if="realty.id"
            class="d-flex align-start mw-400 body-2 px-3 pt-2 pb-1 lighten-2 rounded-lg warning--text"
          >
            <v-icon color="warning lighten-1" left>
              mdi-alert-circle-outline
            </v-icon>
            {{ $t('realty.hint.editWarning') }}
          </span>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="6" sm="4">
          <v-text-field
            v-model.lazy="rent_value"
            v-money="catalogCurrency()"
            :label="$t('realty.formCreate.rentValue')"
            class="amount amount--small inputAmount mx-auto"
            hide-details="auto"
            ref="rentValue"
            type="tel"
            outlined
          />
        </v-col>
        <v-col cols="6" sm="4">
          <v-text-field
            v-model.lazy="sell_value"
            v-money="catalogCurrency()"
            :label="$t('realty.formCreate.sellValue')"
            class="amount amount--small inputAmount mx-auto"
            hide-details="auto"
            ref="sellValue"
            type="tel"
            outlined
          />
        </v-col>
      </v-row>

      <template v-if="showVistaFields">
        <h2 class="mt-10 mb-2">
          {{ $t('realty.formCreate.titles.vista') }}
        </h2>

        <span class="d-flex align-start body-2 pb-2 grey--text text--darken-1">
          <v-icon color="grey darken-1" small left>
            mdi-information-outline
          </v-icon>
          {{ $t('realty.hint.vistaFields') }}
        </span>

        <v-row>
          <v-col cols="12" sm="6">
            <v-select
              v-model="vista_finalidade"
              :label="$t('realty.formCreate.vista.finalidade')"
              :rules="[rules.required]"
              :items="vistaRealtyOptions.finalidade"
              hide-details="auto"
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="vista_status"
              :label="$t('realty.formCreate.vista.status')"
              :rules="[rules.required]"
              :items="vistaRealtyOptions.status"
              hide-details="auto"
              outlined
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" sm="6">
            <v-select
              v-model="vista_situacao"
              :label="$t('realty.formCreate.vista.situacao')"
              :rules="[rules.required]"
              :items="vistaRealtyOptions.situacao"
              hide-details="auto"
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="vista_ocupacao"
              :label="$t('realty.formCreate.vista.ocupacao')"
              :rules="[rules.required]"
              :items="vistaRealtyOptions.ocupacao"
              hide-details="auto"
              outlined
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" sm="6">
            <v-select
              v-model="vista_categoria"
              :label="isVistaCategoriaRequired
                ? $t('realty.formCreate.vista.categoriaRequired')
                : $t('realty.formCreate.vista.categoria')"
              :rules="vistaCategoriaRules"
              :items="vistaRealtyOptions.categoria"
              :hint="$t('realty.hint.vistaCategoria')"
              persistent-hint
              clearable
              outlined
            />
          </v-col>
        </v-row>
      </template>

      <h2 class="mt-10 mb-2">
        {{ $t('realty.formCreate.titles.location') }}
      </h2>

      <v-row>
        <v-col cols="12">
          <AddressSearch
            @address="getAddressData"
            :rules="[rules.required]"
            :value="current_address"
            :getFocus="false"
            ref="addressSearch"
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model="additional_address_data"
            :label="$t('realty.formCreate.addressComplement')"
            outlined
          />
        </v-col>
      </v-row>

      <UpdateRealtyPhotos
        v-if="realty.id"
        :realty-id="realty.id"
        :real-estate-id="photosRealEstateId"
        ref="photos"
      />
      <CreateRealtyPhotos v-else v-model="selectedPhotos" ref="createRealtyPhotos" />

      <RealtyVideos
        v-model="selectedVideos"
        :realty-id="realty.id || null"
        :real-estate-id="photosRealEstateId"
        ref="realtyVideos"
      />
    </v-form>

    <ShowPhotoModal ref="photoModal" />

    <div v-show="!isRealtyLoading" class="fixed white py-3 px-5">
      <v-btn
        @click="save"
        :loading="isLoading"
        :disabled="isRealtyCodeExists"
        color="primary"
        elevation="0"
        large
      >
        {{ $t(`create.${realty.id ? 'update' : 'save'}`) }}
      </v-btn>
    </div>
  </CreateRealtyModal>
</template>

<script>
import CreateRealtyModal from '@/components/app/CreateRealtyModal'
import HTextInput from '@/components/app/HTextInput'
import HCheckbox from '@/components/app/HCheckbox'
import HAutocomplete from '@/components/app/HAutocomplete'
import AddressSearch from '@/components/app/AddressSearch'
import ShowPhotoModal from '@/components/realty/ShowPhotoModal'
import CreateRealtyPhotos from '@/components/realty/CreateRealtyPhotos'
import UpdateRealtyPhotos from '@/components/realty/UpdateRealtyPhotos'
import RealtyVideos from '@/components/realty/RealtyVideos'

import { clearCurrency, checkCurrencyDecimals } from '@/util/helpers'
import { FURNISHING, fromFurnishing, furnishingOptions, toFurnishing } from '@/util/furnishing'
import { mapActions, mapMutations, mapGetters } from 'vuex'
import { code, required } from '@/util/errorHandling'
import { catalogCurrency } from '@/util/catalog'
import { realtyTypes } from '@/util/enums'
import { VMoney } from 'v-money'

export default {
  name: 'RealtyCreateUpdate',

  components: {
    CreateRealtyModal,
    HTextInput,
    HCheckbox,
    HAutocomplete,
    AddressSearch,
    ShowPhotoModal,
    CreateRealtyPhotos,
    UpdateRealtyPhotos,
    RealtyVideos
  },

  directives: {
    money: VMoney
  },

  props: {
    customCallButton: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoading: false,

      code: '',
      description: '',
      painting_status: '',
      furnishing: FURNISHING.NOT_FURNISHED,
      furnishingOptions: furnishingOptions((key) => this.$t(key)),
      trousseau: false,
      square_meters: '',
      private_area_meters: '',
      additional_address_data: '',
      current_address: '',
      realty_type: '',
      realtyTypes,
      customer: {},
      fromAppointment: false,

      rent_value: '',
      sell_value: '',

      vista_categoria: null,
      vista_finalidade: null,
      vista_situacao: null,
      vista_ocupacao: null,
      vista_status: null,

      paintingStatus: [
        { value: 'NEW', name: this.$t('realty.paintingStatus.NEW') },
        { value: 'WORN', name: this.$t('realty.paintingStatus.WORN') },
        { value: 'HOST_CRITERIA', name: this.$t('realty.paintingStatus.HOST_CRITERIA') },
      ],

      address: {},
      selectedPhotos: [],
      selectedVideos: [],

      valid: false,
      rules: { code, required },

      validSize: [
        (v) => !!v || this.$t('rules.required'),
        (v) => v > 0 || this.$t('rules.minValue', { value: 1 }),
        (v) => v <= 30000 || this.$t('rules.maxValue', { value: 30000 }),
      ],

      _timer: null,
      isLoadingCheck: false,
      isRealtyCodeExists: false,

      catalogCurrency,
    }
  },

  mounted() {
    if (!this.isUserLoggedIn) return

    this.fetchCities()
    this.fetchRealEstateCity()
    this.initCustomers()

    if (!this.$refs.createRealtyModalWrapper) return
    this.$watch(() => this.$refs.createRealtyModalWrapper.dialog, (isModalOpen) => !isModalOpen && this.$refs.realtyForm.reset())
    this.$watch(
      () => {
        const createRealtyModalWrapper = this.$refs?.createRealtyModalWrapper
        if (createRealtyModalWrapper) return createRealtyModalWrapper.dialog
      },
      (status) => {
        if (status === false) {
          this.resetRealty()
          const createAppointmentModal = this.$root?.$children[0]?.$refs?.createAppointmentModal
          createAppointmentModal && createAppointmentModal.applyNewRealtyId()
        }
      }
    )
  },

  watch: {
    // trocar de cliente troca a conta da Vista, e com ela as listas e os valores escolhidos
    customer() {
      if (this.realty.id) return

      this.resetVistaFields()
      this.loadVistaRealtyOptions()
    },
  },

  computed: {
    ...mapGetters([
      'realty',
      'cities',
      'realEstateCity',
      'isRealtyLoading',
      'createRealtySuccess',
      'createdRealtyId',
      'addRealtyPhotosSuccess',
      'addRealtyVideosSuccess',
      'appointmentFields',
      'serviceProviderCustomers',
      'vistaRealtyOptions',
    ]),

    /**
     * A imobiliária dona do imóvel, para as rotas de foto. Usuário de imobiliária carrega a própria
     * e o backend ignora o que vier daqui; usuário de prestador de serviço não tem, e trabalha em
     * nome de um cliente - é a mesma expressão que o buildRealty usa no create.
     */
    photosRealEstateId() {
      return (
        this.realty.real_estate?.id ??
        this.appointmentFields.real_estate_id ??
        this.customer.id ??
        null
      )
    },

    realtyFarFromRealEstate() {
      return this.realty.city_id != this.realEstateCity.id
    },

    checkCityFarFromRealEstate() {
      return (
        this.realty.city_id &&
        this.realEstateCity &&
        this.realtyFarFromRealEstate
      )
    },

    isServiceProviderUser() {
      const roles = localStorage.getItem('access_roles')

      if (!roles) return false
      return roles.includes('ROLE_SERVICE_PROVIDER')
    },

    isUserLoggedIn() {
      const token = localStorage.getItem('access_token')
      return !!token
    },

    // o prestador de serviço cadastra para um cliente, e o vocabulário é por conta da Vista
    vistaRealEstateId() {
      return this.appointmentFields.real_estate_id ?? this.customer?.id ?? null
    },

    isVistaPushEnabled() {
      return this.vistaRealtyOptions.push_enabled === true
    },

    // o código do imóvel na Vista é gerado por ela e é a única forma de endereçá-lo lá
    isCodeHidden() {
      return this.isVistaPushEnabled && !this.realty.id
    },

    isCodeReadonly() {
      return !!this.realty.id && this.realty.external_source === 'VISTA'
    },

    // a Vista mantém o campo omitido no update, então eles são enviados só na criação
    showVistaFields() {
      return this.isVistaPushEnabled && !this.realty.id
    },

    // OUTRO é o único tipo sem equivalente na Vista, a categoria dos demais é derivada
    isVistaCategoriaRequired() {
      return this.realty_type === 'OTHER'
    },

    vistaCategoriaRules() {
      return this.isVistaCategoriaRequired ? [this.rules.required] : []
    },
  },

  methods: {
    ...mapMutations(['setRealty']),

    ...mapActions([
      'fetchRealty',
      'fetchCities',
      'createRealty',
      'updateRealty',
      'addRealtyPhotos',
      'addRealtyVideos',
      'fetchRealties',
      'searchRealties',
      'fetchRealEstateCity',
      'listServiceProviderCustomers',
      'fetchVistaRealtyOptions',
    ]),

    trousseauOrFurnishedChanged() {
      return (
        this.realty.trousseau != this.notChangedRealty.trousseau ||
        this.realty.furnished != this.notChangedRealty.furnished
      )
    },

    async save() {
      if (!this.$refs.realtyForm.validate() || !this.valid) return

      this.isLoading = true

      try {
        this.realty.id
          ? await this.updateRealty(this.buildRealty())
          : await this.createRealty(this.buildRealty())

        if (!this.createRealtySuccess) return
        if (!(await this.uploadSelectedPhotos())) return
        if (!(await this.uploadSelectedVideos())) return

        this.resetRealty()
        this.fetchRealties()
        this.$refs.createRealtyModalWrapper.closeModal()
      } finally {
        this.isLoading = false
      }
    },

    /**
     * As fotos sobem depois de o imóvel existir, pelo mesmo /addPhotos que o UpdateRealtyPhotos já
     * usa. O create não as carrega: o payload iria com todas as imagens em base64, e com o push da
     * Vista ligado uma foto recusada lá desfaria a criação do imóvel inteiro - o insert de foto roda
     * na transação do create.
     *
     * Só o create passa por aqui. Na edição quem cuida das fotos é o UpdateRealtyPhotos, que sobe
     * cada seleção na hora.
     */
    async uploadSelectedPhotos() {
      if (this.realty.id || this.selectedPhotos.length === 0) return true

      const realtyPhotos = await this.preparePhotos()
      await this.addRealtyPhotos({
        realtyId: this.createdRealtyId,
        // Mesmo id que o create manda: usuário de prestador não tem imobiliária própria, e sem ele
        // o backend não acha o imóvel do cliente
        real_estate_id: this.photosRealEstateId,
        realtyPhotos
      })

      // A action mostra o erro e não propaga, então o resultado vem pelo store. Sem foto gravada o
      // modal fica aberto: o imóvel já existe, e fechar aqui faria o usuário perder a seleção.
      return this.addRealtyPhotosSuccess
    },

    /**
     * Os vídeos que o usuário colou durante a criação sobem depois de o imóvel existir, pelo mesmo
     * /addVideos que a edição usa: ele endereça pelo id do imóvel, que só existe agora.
     *
     * Só o create passa por aqui - na edição o RealtyVideos envia cada link na hora.
     */
    async uploadSelectedVideos() {
      if (this.realty.id || this.selectedVideos.length === 0) return true

      await this.addRealtyVideos({
        realtyId: this.createdRealtyId,
        real_estate_id: this.photosRealEstateId,
        realtyVideos: this.selectedVideos
      })

      // Sem vídeo gravado o modal fica aberto: o imóvel já existe, e fechar perderia os links
      return this.addRealtyVideosSuccess
    },

    buildRealty() {
      let realty = {}

      if (this.realty.id) {
        realty = {
          id: this.realty.id,
          code: this.code,
          description: this.description,
          painting_status: this.painting_status,
          trousseau: this.trousseau,
          ...fromFurnishing(this.furnishing),
          square_meters: this.square_meters,
          private_area_meters: this.private_area_meters,
          additional_address_data: this.additional_address_data,
          address: this.current_address,
          realty_type: this.realty_type,
          rent_value: clearCurrency(this.rent_value),
          sell_value: clearCurrency(this.sell_value),
          real_estate_id: this.realty.real_estate.id ?? null,
        }
      } else {
        realty = {
          description: this.description,
          painting_status: this.painting_status,
          trousseau: this.trousseau,
          ...fromFurnishing(this.furnishing),
          square_meters: this.square_meters,
          private_area_meters: this.private_area_meters,
          additional_address_data: this.additional_address_data,
          address: this.current_address,
          realty_type: this.realty_type,
          rent_value: clearCurrency(this.rent_value),
          sell_value: clearCurrency(this.sell_value),
          real_estate_id: this.appointmentFields.real_estate_id ?? this.customer.id ?? null,
        }

        // com o push ligado quem gera o código é a Vista, qualquer valor enviado é descartado
        if (this.isVistaPushEnabled) {
          realty.vista_categoria = this.vista_categoria
          realty.vista_finalidade = this.vista_finalidade
          realty.vista_situacao = this.vista_situacao
          realty.vista_ocupacao = this.vista_ocupacao
          realty.vista_status = this.vista_status
        } else {
          realty.code = this.code
        }
      }

      return realty
    },

    resetRealty() {
      this.code = ''
      this.description = ''
      this.painting_status = ''
      this.furnishing = FURNISHING.NOT_FURNISHED
      this.trousseau = false
      this.square_meters = ''
      this.private_area_meters = ''
      this.additional_address_data = ''
      this.current_address = ''
      this.realty_type = ''
      this.rent_value = ''
      this.sell_value = ''
      this.resetVistaFields()
      this.$refs.createRealtyPhotos && this.$refs.createRealtyPhotos.resetPhotos()
      this.$refs.realtyVideos && this.$refs.realtyVideos.resetVideos()
      this.setRealty({})
    },

    getAddressData(data) {
      this.address = data
      this.realty.address = data.address
      this.current_address = data.address
    },

    getUrl(photoIndex) {
      const photo = this.selectedPhotos[photoIndex]
      if (!photo) return
      return URL.createObjectURL(photo)
    },

    showPhoto(url) {
      this.$refs.photoModal.openModal(url)
    },

    // Devolve as fotos em base64 em vez de gravar em this.realty: o payload do create não leva
    // fotos, e era essa atribuição que fazia a seleção do usuário se perder no caminho.
    async preparePhotos() {
      return await Promise.all(
        this.selectedPhotos.map((photo) => this.getRealtyPhotoPromise(photo))
      )
    },

    getRealtyPhotoPromise(file) {
      return new Promise((resolve, reject) => {
        let reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onloadend = () => {
          let realtyPhoto = {}
          realtyPhoto['filename'] = file.name.trim()
          realtyPhoto['photo_data'] = reader.result
            .replace('data:', '')
            .replace(/^.+,/, '')
          resolve(realtyPhoto)
        }
        reader.onerror = (error) => reject(error)
      })
    },

    selectPhotos() {
      this.selectedPhotos = Array.from(this.$refs.inputUpload.files)
    },

    openRealtyCreateModal(fromAppointment) {
      this.fromAppointment = fromAppointment ?? false
      this.loadVistaRealtyOptions()
      this.$refs.createRealtyModalWrapper?.openModal()
    },

    // as listas são lidas na Vista a cada chamada, então são buscadas na abertura do modal
    loadVistaRealtyOptions() {
      this.fetchVistaRealtyOptions(this.vistaRealEstateId)
    },

    resetVistaFields() {
      this.vista_categoria = null
      this.vista_finalidade = null
      this.vista_situacao = null
      this.vista_ocupacao = null
      this.vista_status = null
    },

    search(term) {
      this.searchRealties({ searchText: term })
        .then(({ data }) => {
          data.find((realty) => realty.code === term)
            ? (this.isRealtyCodeExists = true)
            : (this.isRealtyCodeExists = false)
        })
        .finally(() => {
          this.isLoadingCheck = false
        })
    },

    checkRealtyCode(term) {
      if (this.isVistaPushEnabled || this.isCodeReadonly) return
      if (this.code === this.realty.code) return

      this.isLoadingCheck = true
      this.isRealtyCodeExists = false

      clearTimeout(this._timer)

      this._timer = setTimeout(() => {
        this.search(term)
      }, 2000)
    },

    async fetchRealtyData(id) {
      await this.fetchRealty(id)

      this.$nextTick(() => {
        this.code = this.realty.code
        this.description = this.realty.description
        this.painting_status = this.realty.painting_status
        this.trousseau = this.realty.trousseau
        // Stays empty for a legacy realty with no furnishing, so saving it leaves furnishing as is
        this.furnishing = toFurnishing(this.realty.furnished, this.realty.semi_furnished)
        this.square_meters = this.realty.square_meters
        this.private_area_meters = this.realty.private_area_meters
        this.additional_address_data = this.realty.additional_address_data
        this.current_address = this.realty.address
        this.$refs.addressSearch.googleAddress.address = this.realty.address
        this.realty_type = this.realty.realty_type

        this.fillPrices()
      })
    },

    fillPrices() {
      // to fill price at lazy v-model
      if (!this.$refs.rentValue || !this.$refs.sellValue) return
      this.$refs.rentValue.$refs.input.value = checkCurrencyDecimals(this.realty.rent_value)
      this.$refs.sellValue.$refs.input.value = checkCurrencyDecimals(this.realty.sell_value)
    },

    initCustomers() {
      if (!this.isServiceProviderUser) return

      this.listServiceProviderCustomers()
    },
  },
}
</script>

<style scoped>
.fixed {
  display: flex;
  position: fixed;
  justify-content: flex-end;
  width: calc(100% - 15px);
  border-top: 1px solid #dddddd;
  bottom: 0;
  right: 0;
  left: 0;
  box-shadow: 0 -1px 0 0 rgba(100, 100, 100, 0.2);
}
</style>
